'use client'

import { referencesProductArticlesFilteredAtom } from '@/feature/product/services/referencesProduct.store'
import type {
  ProductAvailabilities_PickupPointOfSaleFragment,
  ReferencesProduct_CurrentUserQuery,
  ReferencesProductTableRows_PriceAndStockQuery,
} from '@gql-generated/graphql'
import { useAtomValue } from 'jotai'
import { memo, useMemo } from 'react'
import {
  ReferencesProductTableProvider,
  useReferencesProductTableContextData,
} from './ReferencesProductContext'
import { ReferencesProductRow } from './ReferencesProductRow.client'

export type ReferencesProductRowsProps = {
  isArticlesPriceAndStockLoading: boolean
  currentUser?: ReferencesProduct_CurrentUserQuery['currentUser'] | null
  articlesPriceAndStock?: ReferencesProductTableRows_PriceAndStockQuery['productsByReferences']
  pickupPointOfSale?: ProductAvailabilities_PickupPointOfSaleFragment | null
}

function ReferencesProductRowsComponent({
  isArticlesPriceAndStockLoading,
  currentUser,
  articlesPriceAndStock,
  pickupPointOfSale,
}: ReferencesProductRowsProps) {
  const articlesFiltered = useAtomValue(referencesProductArticlesFilteredAtom)

  // Compute all context data using the custom hook
  const contextValue = useReferencesProductTableContextData({
    isArticlesPriceAndStockLoading,
    currentUser,
    articlesPriceAndStock,
    pickupPointOfSale,
  })

  // Memoize articles data enriched with price & stock
  const articlesWithPriceAndStock = useMemo(() => {
    if (!articlesFiltered || !articlesPriceAndStock) return articlesFiltered

    const priceAndStockMap = new Map(articlesPriceAndStock.map((item) => [item.reference, item]))

    return articlesFiltered.map((article) => ({
      ...article,
      ...priceAndStockMap.get(article.reference),
    }))
  }, [articlesFiltered, articlesPriceAndStock])

  if (!articlesWithPriceAndStock) {
    return null
  }

  return (
    <ReferencesProductTableProvider value={contextValue}>
      {articlesWithPriceAndStock.map((article, index) => (
        <ReferencesProductRow key={article.reference} article={article} position={index} />
      ))}
    </ReferencesProductTableProvider>
  )
}

export const ReferencesProductRows = memo(ReferencesProductRowsComponent)
